import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Compass, Wrench, ArrowRight, Users, Clock3, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import EventCard from "@/components/EventCard";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1616680214084-22670de1bc82?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function HomePage() {
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();
  const { t } = useLang();

  const load = useCallback(() => api.get("/events").then((r) => setEvents(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <section className="relative overflow-hidden bg-slate-900">
        <img src={HERO_IMG} alt="Relawan Indonesia" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/70 to-emerald-950/60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-24 lg:pt-36 lg:pb-32">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-bold px-4 py-1.5 rounded-full mb-6">
              <Sparkles className="w-3.5 h-3.5" /> {t("hero_badge")}
            </span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
              {t("hero_title_1")}<br /><span className="text-emerald-400">{t("hero_title_2")}</span>
            </h1>
            <p className="text-slate-300 text-base md:text-lg mt-6 leading-relaxed max-w-xl">
              {t("hero_sub")}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button data-testid="hero-matchmaker-btn" onClick={() => navigate("/matchmaker")} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-7 h-12 text-base">
                <Sparkles className="w-5 h-5 mr-2" /> {t("hero_cta_match")}
              </Button>
              <Button data-testid="hero-explore-btn" onClick={() => navigate("/jelajahi")} variant="outline" className="rounded-full border-slate-500 bg-white/5 text-white hover:bg-white/15 hover:text-white font-bold px-7 h-12 text-base">
                <Compass className="w-5 h-5 mr-2" /> {t("hero_cta_explore")}
              </Button>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-4 mt-12">
              {[{ icon: Users, val: "45", label: t("stat_volunteers") }, { icon: Clock3, val: "270", label: t("stat_hours") }, { icon: CheckCircle2, val: "2", label: t("stat_projects") }, { icon: Users, val: "±350", label: t("stat_beneficiaries") }].map((s) => (
                <div key={s.label}>
                  <p className="font-heading font-black text-2xl text-white">{s.val}</p>
                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Sparkles, title: t("nav_match"), desc: t("feature_match_desc"), to: "/matchmaker", testid: "feature-matchmaker" },
            { icon: Wrench, title: "Social Action Planner", desc: t("feature_planner_desc"), to: "/solusi", testid: "feature-solusi" },
            { icon: Compass, title: t("nav_explore"), desc: t("feature_explore_desc"), to: "/jelajahi", testid: "feature-jelajahi" },
          ].map((f, i) => (
            <motion.button
              key={f.title}
              data-testid={f.testid}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              onClick={() => navigate(f.to)}
              className="card-lift text-left bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-7"
            >
              <span className="inline-flex bg-emerald-50 text-emerald-600 rounded-2xl p-3 mb-4"><f.icon className="w-6 h-6" /></span>
              <h3 className="font-heading font-extrabold text-lg text-slate-900">{f.title}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{f.desc}</p>
              <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-sm mt-4">{t("open")} <ArrowRight className="w-4 h-4" /></span>
            </motion.button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-heading font-black text-2xl md:text-3xl text-slate-900 tracking-tight">{t("nearby_title")}</h2>
            <p className="text-slate-500 mt-1 text-sm md:text-base">{t("nearby_sub")}</p>
          </div>
          <Button data-testid="see-all-events-btn" variant="ghost" onClick={() => navigate("/jelajahi")} className="rounded-full font-bold text-emerald-700 hover:bg-emerald-50">
            {t("see_all")} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.slice(0, 6).map((e) => <EventCard key={e.id} event={e} onRegistered={load} />)}
        </div>
      </section>
    </div>
  );
}
